import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Heart } from 'lucide-react';

const Footer: React.FC = () => {
  const { t } = useTranslation();

  return (
    <footer className="lux-ink border-t border-white/10 text-white py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-8">
          <div className="md:col-span-1">
            <div className="flex items-center space-x-2 mb-6">
                  <img
                    src="/scanvcf_icon_transparent_pack/scanvcf_icon_square_transparent_32.png"
                alt="ScanVCF"
                className="w-8 h-8 rounded-lg"
              />
              <span className="text-xl font-bold">ScanVCF</span>
            </div>
            <p className="text-gray-400 mb-6">
              {t('landing.footer.description', 'Create and share professional digital business cards with QR codes.')}
            </p>
            <div className="flex space-x-4">
              <a href="https://twitter.com/scanvcf" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-[#c8a45e] transition-colors" aria-label="Twitter">
                <span className="sr-only">Twitter</span>
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path d="M8.29 20.251c7.547 0 11.675-6.253 11.675-11.675 0-.178 0-.355-.012-.53A8.348 8.348 0 0022 5.92a8.19 8.19 0 01-2.357.646 4.118 4.118 0 001.804-2.27 8.224 8.224 0 01-2.605.996 4.107 4.107 0 00-6.993 3.743 11.65 11.65 0 01-8.457-4.287 4.106 4.106 0 001.27 5.477A4.072 4.072 0 012.8 9.713v.052a4.105 4.105 0 003.292 4.022 4.095 4.095 0 01-1.853.07 4.108 4.108 0 003.834 2.85A8.233 8.233 0 012 18.407a11.616 11.616 0 006.29 1.84" />
                </svg>
              </a>
              <a href="https://facebook.com/scanvcf" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-[#c8a45e] transition-colors" aria-label="Facebook">
                <span className="sr-only">Facebook</span>
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
                </svg>
              </a>
              <a href="https://instagram.com/scanvcf" target="_blank" rel="noopener noreferrer" className="text-gray-500 hover:text-[#c8a45e] transition-colors" aria-label="Instagram">
                <span className="sr-only">Instagram</span>
                <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24">
                  <path fillRule="evenodd" d="M12.017 0C5.396 0 .029 5.367.029 11.987c0 6.62 5.367 11.987 11.988 11.987s11.987-5.367 11.987-11.987C24.004 5.367 18.637.001 12.017.001zM8.449 16.988c-1.297 0-2.448-.49-3.323-1.297C4.198 14.895 3.708 13.744 3.708 12.447s.49-2.448 1.418-3.323c.875-.807 2.026-1.297 3.323-1.297s2.448.49 3.323 1.297c.928.875 1.418 2.026 1.418 3.323s-.49 2.448-1.418 3.244c-.875.807-2.026 1.297-3.323 1.297zm7.83-9.781h-1.68V5.526h1.68v1.681zm-1.68 9.781c-1.297 0-2.448-.49-3.323-1.297-.928-.875-1.418-2.026-1.418-3.323s.49-2.448 1.418-3.323c.875-.807 2.026-1.297 3.323-1.297s2.448.49 3.323 1.297c.928.875 1.418 2.026 1.418 3.323s-.49 2.448-1.418 3.244c-.875.807-2.026 1.297-3.323 1.297z" clipRule="evenodd" />
                </svg>
              </a>
            </div>
          </div>
          
          <div>
            <h3 className="font-semibold mb-4">{t('landing.footer.product', 'Product')}</h3>
            <ul className="space-y-2 text-gray-400">
              <li><Link to="/features" className="hover:text-white transition-colors">{t('landing.footer.features', 'Features')}</Link></li>
              <li>
                <Link
                  to="/#pricing"
                  className="hover:text-white transition-colors"
                  onClick={(e) => {
                    e.preventDefault();
                    window.location.href = '/#pricing';
                  }}
                >
                  {t('landing.footer.pricing', 'Pricing')}
                </Link>
              </li>
              <li><a href="https://play.google.com/store/apps/details?id=com.scanvcf.app" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors">{t('landing.footer.download', 'Download')}</a></li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-semibold mb-4">{t('landing.footer.support', 'Support')}</h3>
            <ul className="space-y-2 text-gray-400">
              <li><Link to="/help" className="hover:text-white transition-colors">{t('landing.footer.help', 'Help Center')}</Link></li>
              <li><Link to="/contact" className="hover:text-white transition-colors">{t('landing.footer.contact', 'Contact Us')}</Link></li>
            </ul>
          </div>
          
          <div>
            <h3 className="font-semibold mb-4">{t('landing.footer.legal', 'Legal')}</h3>
            <ul className="space-y-2 text-gray-400">
              <li><a href="/privacy.html" className="hover:text-white transition-colors">{t('landing.footer.privacy', 'Privacy Policy')}</a></li>
              <li><a href="/terms.html" className="hover:text-white transition-colors">{t('landing.footer.terms', 'Terms of Service')}</a></li>
              <li><a href="/delete-account.html" className="hover:text-white transition-colors">{t('landing.footer.delete', 'Delete Account')}</a></li>
              <li><a href="/refund-policy.html" className="hover:text-white transition-colors">{t('landing.footer.refund', 'Refund Policy')}</a></li>
            </ul>
          </div>
        </div>
        
        <div className="border-t border-white/10 mt-12 pt-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <p className="text-gray-400 text-sm">
              &copy; 2025 ScanVCF. All rights reserved.
            </p>
            <div className="flex items-center space-x-4 mt-4 md:mt-0">
              <span className="text-gray-400 text-sm">
                {t('landing.footer.madeWith', 'Made with')} <Heart className="inline w-4 h-4 text-red-500" /> {t('landing.footer.by', 'by')} <a href="https://t2design.com.au/" target="_blank" rel="noopener noreferrer" className="text-gray-400 hover:text-white transition-colors">T2DESIGN</a>
              </span>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
